// DateTimePicker.tsx
import { useState, useEffect } from "react";
import { 
  Box, 
  Button, 
  Grid, 
  Typography 
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ru } from "date-fns/locale/ru";
import { format } from "date-fns";
import "./DateTimePicker.css";
import '../styles/variables.css'
import { ServiceResponseDTO, AvailableSlotDTO } from './types/ZaprosDTO';
import { ApiService } from '../services/api.service';

type Props = {
  showTimePicker: boolean;
  onConfirm: (date: string, time: string) => void;
  onCancel: () => void;
  selectedService: ServiceResponseDTO;
  bookingQuantity: number;
};

export const DateTimePicker = ({ 
  showTimePicker,
  onConfirm,
  onCancel,
  selectedService,
  bookingQuantity
}: Props) => {
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<Date | null>(null);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlotDTO[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    const fetchSlots = async () => {
      if (!date || !selectedService) return;
      
      setLoadingSlots(true);
      try {
        const formattedDate = format(date, 'yyyy-MM-dd');
        const slots = await ApiService.getAvailableSlots(
          selectedService.service_id,
          formattedDate
        );
        setAvailableSlots(slots);
      } catch (error) {
        console.error("Ошибка загрузки слотов:", error);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [date, selectedService]);

  const handleConfirm = () => {
    if (!date) return;
  
    if (!selectedService.person_based_pricing) {
      if (!time) return;
  
      const selectedSlot = availableSlots.find(slot => {
        const slotTime = new Date(slot.start_time);
        return format(time, "HH:mm:ss") === format(slotTime, "HH:mm:ss");
      });
  
      if (!selectedSlot || selectedSlot.available_quantity === 0) {
        alert('На выбранное время всё забронировано');
        return;
      }
  
      if (bookingQuantity > selectedSlot.available_quantity) {
        alert(`Доступно только ${selectedSlot.available_quantity} единиц`);
        return;
      }
    } else {
      const dailySlots = availableSlots.filter(slot => 
        new Date(slot.start_time).getDate() === date.getDate()
      );
  
      const totalAvailable = dailySlots.reduce(
        (sum, slot) => sum + slot.available_quantity, 0
      );
  
      if (totalAvailable === 0) {
        alert('На выбранную дату нет свободных мест');
        return;
      }
  
      if (bookingQuantity > totalAvailable) {
        alert(`Нет мест на выбранную дату`);
        return;
      }
    }
  
    const formattedDate = format(date, "yyyy-MM-dd");
    const formattedTime = selectedService.person_based_pricing 
      ? "08:00:00" 
      : format(time!, "HH:mm:ss");
  
    onConfirm(formattedDate, formattedTime);
  };

  const renderTimePicker = () => {
    if (!showTimePicker) return null;

    return (
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ 
          mb: 2, 
          textAlign: 'center',
          fontWeight: 500,
          color: 'text.secondary',
          fontSize: '1.25rem'
        }}>
          Доступное время
        </Typography>
        
        {loadingSlots ? (
          <Typography variant="body2" align="center">Загрузка...</Typography>
        ) : availableSlots.length === 0 ? (
          <Typography variant="body2" align="center">Нет доступных слотов</Typography>
        ) : (
          <Box sx={{
            display: 'flex',
            overflowX: 'auto',
            pb: 2,
            mx: { xs: -2, md: 0 },
            px: { xs: 2, md: 0 },
            '&::-webkit-scrollbar': { height: '4px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
            '&::-webkit-scrollbar-thumb': { background: '#888' },
          }}>
            {availableSlots
              .filter(slot => slot.is_available)
              .map((slot) => {
                const slotDate = new Date(slot.start_time);
                const isSelected = time?.getTime() === slotDate.getTime();

                return (
                  <Box
                    key={slot.start_time}
                    onClick={() => setTime(slotDate)}
                    sx={{
                      minWidth: 100,
                      mx: 1,
                      borderRadius: '12px',
                      flexShrink: 0,
                      py: 1.5,
                      px: 2,
                      transition: 'all 0.2s',
                      border: isSelected ? '2px solid #115293' : '2px solid #e0e0e0',
                      backgroundColor: isSelected ? '#115293' : 'white',
                      color: isSelected ? 'white' : 'text.primary',
                      cursor: 'pointer',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {format(slotDate, 'HH:mm', { locale: ru })}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {slot.available_quantity} доступно
                    </Typography>
                  </Box>
                );
              })}
          </Box>
        )}
      </Grid>
    );
  };

  return (
    <Box className="calendar-container" sx={{ 
      p: 3, 
      bgcolor: 'background.paper',
      borderRadius: '8px',
      boxShadow: 1,
      marginBottom: '80px',
      zIndex: 'modal', 
      position: 'relative',
      overflow: 'visible' 
    }}>
      <Typography variant="h5" gutterBottom sx={{ 
        textAlign: 'center', 
        mb: '5px', // Отступ снизу 5px
        mt: '5px',  // Отступ сверху 5px
        fontWeight: 600,
        color: 'text.primary',
        fontSize: '1.2rem', // Уменьшаем размер шрифта
        className: 'choose-date-header',
      }}>
        {showTimePicker ? 'Выберите дату и время' : 'Выберите дату'}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={showTimePicker ? 6 : 12}>
          <Box sx={{ border: '1px solid #ddd', borderRadius: '4px' }}>
            <DatePicker
              selected={date}
              onChange={setDate}
              dateFormat="dd.MM.yyyy"
              locale={ru}
              inline
              minDate={new Date()}
              calendarClassName="custom-calendar"
            />
          </Box>
        </Grid>

        {renderTimePicker()}
      </Grid>

      {/* Измененный блок кнопок */}
      <Box 
        className="action-buttons"
        sx={{ 
          mt: 2,
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid #eee',
          pt: 2,
          gap: 2,
          position: 'sticky',
          bottom: 0,
          background: 'white',
          zIndex: 100,
        }}
      >
        <Button 
          variant="outlined" 
          onClick={onCancel}
          sx={{ minWidth: 120, flex: 1, textTransform: 'none' }}
        >
          Назад
        </Button>
        <Button 
          variant="contained" 
          onClick={handleConfirm}
          disabled={!date || (showTimePicker && !time)}
          sx={{ minWidth: 120, flex: 1, textTransform: 'none', backgroundColor: '#115293', }}
        >
          Продолжить
        </Button>
      </Box>
    </Box>
  );
};